import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { ControlContainer, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { combineLatest, Subscription } from 'rxjs';
import { filter, first, switchMap, tap } from 'rxjs/operators';

import { CopyService } from '@amfam/shared/utility/shared-services';
import { ValidationService } from '@amfam/ui-kit';

import { AnalyticsEventType } from '../+state/quick-quote-feature/quick-quote-enum';
import { QuickQuoteFeatureFacade } from '../+state/quick-quote-feature/quick-quote-feature.facade';

@Component({
  selector: 'quote-vehicle-details',
  templateUrl: './quote-vehicle-details.component.html',
  styleUrls: ['./quote-vehicle-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class QuoteVehicleDetailsComponent implements OnInit, OnDestroy, AfterViewInit {
  private subscription: Subscription = new Subscription();
  quoteVehicleForm: UntypedFormGroup;
  years$ = this.quickQuoteFeatureFacade.vehicleYears$;
  vehicleMakes$ = this.quickQuoteFeatureFacade.vehicleMakes$;
  vehicleModels$ = this.quickQuoteFeatureFacade.vehicleModels$;
  vehicleSeries$ = this.quickQuoteFeatureFacade.vehicleSeries$;
  isVehicleLoading$ = this.quickQuoteFeatureFacade.isVehicleLoading$;
  vehicleError$ = this.quickQuoteFeatureFacade.vehicleError$;
  isDisabled = true;
  validationErrors: { noData: string; invalidYear: string };

  constructor(
    public quickQuoteFeatureFacade: QuickQuoteFeatureFacade,
    public controlContainer: ControlContainer,
    private changeDetector: ChangeDetectorRef,
    private copyService: CopyService
  ) {}

  ngAfterViewInit(): void {
    this.quickQuoteFeatureFacade.sendAnalytics(AnalyticsEventType.QUICK_QUOTE_START);
  }

  ngOnInit(): void {
    this.validationErrors = this.copyService.getCopy('policy.quickQuoteValidationErrors');
    this.quoteVehicleForm = this.controlContainer.control as UntypedFormGroup;
    this.quoteVehicleForm.get('model').disable();
    this.subscription.add(
      this.vehicleSeries$.subscribe(val => {
        if (!(val?.length > 0)) {
          this.quoteVehicleForm.removeControl('series');
          this.quoteVehicleForm.updateValueAndValidity();
        }
      })
    );
  }

  getVehicleMake() {
    const { year } = this.quoteVehicleForm.getRawValue();
    if (this.validateYear(year)) {
      this.handleNoData();
      this.quickQuoteFeatureFacade.loadVehicleMake(year);
      this.quoteVehicleForm.controls['make'].setValue('unset');
      this.quoteVehicleForm.controls['model'].setValue('unset');
      this.quoteVehicleForm.controls['model'].disable();
    }
  }

  handleNoData() {
    this.isVehicleLoading$
      .pipe(
        filter(loading => loading),
        switchMap(() => combineLatest([this.vehicleError$, this.isVehicleLoading$])),
        filter(([_, isLoading]) => !isLoading),
        first(),
        tap(([error, _]) => error && this.setValidationMessage(this.validationErrors.noData))
      )
      .subscribe();
  }

  validateYear(year: number): boolean {
    const yearControl = this.quoteVehicleForm.get('year');
    const currentYear = new Date().getFullYear();
    if (year > currentYear + 1) {
      yearControl.setErrors({
        error: `${this.validationErrors.invalidYear} ${currentYear + 2}`
      });
      return false;
    }
    return true;
  }

  setValidationMessage(message: string) {
    const yearControl = this.quoteVehicleForm.get('year');
    yearControl.setErrors({
      error: message
    });
    this.changeDetector.detectChanges();
  }

  getVehicleModel() {
    this.quoteVehicleForm.controls['model'].setValue('unset');
    this.quoteVehicleForm.controls['model'].enable();
    const { year, make } = this.quoteVehicleForm.getRawValue();
    this.quickQuoteFeatureFacade.loadVehicleModels(year, make);
  }

  getVehicleSeries() {
    const { year, make, model } = this.quoteVehicleForm.getRawValue();
    this.quickQuoteFeatureFacade.loadVehicleSeries(year, make, model);
    this.quoteVehicleForm.addControl(
      'series',
      new UntypedFormControl('unset', Validators.compose([ValidationService.unsetStringValidator]))
    );
    this.quoteVehicleForm.updateValueAndValidity();
  }

  onSeriesSelected() {
    const vehicle = this.quoteVehicleForm.getRawValue();
    if (vehicle)
      this.quickQuoteFeatureFacade.addNewVehicle(
        vehicle.year,
        vehicle.make,
        vehicle.model,
        vehicle.series
      );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.quoteVehicleForm.patchValue({
      year: 'unset',
      make: 'unset',
      model: 'unset',
      series: 'unset'
    });
  }
}
